import { useParams, Navigate, Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { Check, ArrowRight, Star, BookOpen, GitCompare } from "lucide-react";
import axios from "axios";
import { API } from "@/utils/constants";
import { SEO, JsonLd } from "@/components/common/SEO";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { AffiliateCTA } from "@/components/ui/AffiliateCTA";
import { LinkCardGrid } from "@/components/common/LinkCardGrid";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import {
  COMPARISON_PAIRS, canonicalPair,
  get50kPrice, getFirmStrengths, getFirmWeaknesses,
  generateFAQ, generateTableRows, getComparisonsForFirm,
  formatConsistency,
} from "@/utils/comparisonEngine";
import { adaptApiFirm, adaptBranding } from "@/utils/comparisonEngine";
import { parsePrice as parsePriceUtil } from "@/utils/priceUtils";
import { formatFirmDisplay } from "@/utils/formatFirmDisplay";
import { computeFFScore, getScoreColor, COMPONENT_META, getComponentReason, getBarColor } from "@/utils/furyScore";
import { FIRM_SLUG_TO_REVIEW_SLUG } from "@/data/firmRegistry";

/**
 * Validate and resolve route slug into a pair of firm slugs.
 * Uses COMPARISON_PAIRS for route validation (synchronous — no data fetch).
 */
function resolveRoutePair(slugs) {
  if (!slugs) return null;
  // Try all possible "-vs-" split points (firm slugs may contain hyphens)
  for (let i = 1; i < slugs.length; i++) {
    const vsIdx = slugs.indexOf("-vs-", i - 1);
    if (vsIdx === -1) break;
    const a = slugs.substring(0, vsIdx);
    const b = slugs.substring(vsIdx + 4);
    if (!a || !b) continue;
    // Check if this pair exists in our valid pairs list
    const isPairValid = COMPARISON_PAIRS.some(
      p => (p.a === a && p.b === b) || (p.a === b && p.b === a)
    );
    if (isPairValid) {
      const canonical = canonicalPair(a, b);
      return { a, b, canonical, isCanonical: `${a}-vs-${b}` === canonical };
    }
  }
  return null;
}

export default function DynamicComparisonPage() {
  const { slugs } = useParams();
  const pair = resolveRoutePair(slugs || "");

  // Invalid pair → 404 (synchronous, no loading needed)
  if (!pair) return <NotFoundComparison />;

  // Non-canonical order → redirect (synchronous)
  if (!pair.isCanonical) return <Navigate to={`/compare/${pair.canonical}`} replace />;

  return <ComparisonContent pair={pair} />;
}

/**
 * The main comparison content — fetches data from API.
 * Separated so the parent can handle routing logic synchronously.
 */
function ComparisonContent({ pair }) {
  const [firmA, setFirmA] = useState(null);
  const [firmB, setFirmB] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    axios.get(`${API}/firms/comparison?slugs=${pair.a},${pair.b}`)
      .then(res => {
        const firms = res.data;
        if (!firms || firms.length < 2) {
          setError("Comparison data not available.");
          return;
        }
        // API returns firms in requested slug order
        setFirmA(adaptApiFirm(firms[0]));
        setFirmB(adaptApiFirm(firms[1]));
      })
      .catch(() => setError("Failed to load comparison data. Please try again."))
      .finally(() => setLoading(false));
  }, [pair.a, pair.b]);

  if (loading) return <ComparisonSkeleton />;
  if (error || !firmA || !firmB) return <ComparisonError message={error} />;

  const brandA = adaptBranding(firmA);
  const brandB = adaptBranding(firmB);
  const tableRows = generateTableRows(firmA, firmB);
  const faqData = generateFAQ(firmA, firmB);
  const strengthsA = getFirmStrengths(firmA);
  const strengthsB = getFirmStrengths(firmB);
  const weaknessesA = getFirmWeaknesses(firmA);
  const weaknessesB = getFirmWeaknesses(firmB);
  const priceA = get50kPrice(firmA);
  const priceB = get50kPrice(firmB);
  const isAExternal = firmA.isExternal;
  const isBExternal = firmB.isExternal;

  // Review page slugs (may differ from firm DB slug, e.g. aquafutures → aqua-futures)
  const reviewSlugA = FIRM_SLUG_TO_REVIEW_SLUG[pair.a] || pair.a;
  const reviewSlugB = FIRM_SLUG_TO_REVIEW_SLUG[pair.b] || pair.b;

  // Related comparisons for internal linking (uses COMPARISON_PAIRS — sync)
  const relatedA = getComparisonsForFirm(pair.a).filter(c => c.slug !== pair.canonical).slice(0, 3);
  const relatedB = getComparisonsForFirm(pair.b).filter(c => c.slug !== pair.canonical).slice(0, 3);

  const rawTitle = `${firmA.name} vs ${firmB.name} (2026)`;
  const title = rawTitle.length + 15 > 60 ? `${firmA.name} vs ${firmB.name}` : rawTitle;
  const description = `${firmA.name} vs ${firmB.name} compared: fees, drawdown rules, profit split, and payout speed. Find the better prop firm for your style.`;
  const canonical = `/compare/${pair.canonical}`;

  const itemListSchema = { "@context": "https://schema.org", "@type": "ItemList", "itemListElement": [
    { "@type": "ListItem", "position": 1, "item": { "@type": "Organization", "name": firmA.name, "url": `https://futuresfury.com/firms/${pair.a}` } },
    { "@type": "ListItem", "position": 2, "item": { "@type": "Organization", "name": firmB.name, "url": `https://futuresfury.com/firms/${pair.b}` } },
  ]};
  const faqSchema = { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: faqData.map(f => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) };
  const breadcrumbSchema = { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://futuresfury.com/" },
    { "@type": "ListItem", position: 2, name: "Compare Firms", item: "https://futuresfury.com/best-futures-prop-firms" },
    { "@type": "ListItem", position: 3, name: `${firmA.name} vs ${firmB.name}` },
  ]};

  return (
    <TooltipProvider delayDuration={200}>
    <div className="min-h-screen surface-page">
      <SEO title={title} description={description} canonical={canonical} />
      <JsonLd data={faqSchema} />
      <JsonLd data={breadcrumbSchema} />
      <JsonLd data={itemListSchema} />
      <Header />

      <main id="main-content" tabIndex={-1} className="pt-28 pb-16 px-4 sm:px-6" data-testid="comparison-page">
        <div className="max-w-4xl mx-auto">

          {/* H1 + Intro */}
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4" data-testid="comparison-h1">
            {firmA.name} vs {firmB.name}: Which Firm Is Better in 2026?
          </h1>

          <p className="text-base text-slate-600 dark:text-slate-300 leading-relaxed mb-4">
            <Link to={`/firms/${pair.a}`} className="text-[#1E88E5] hover:underline font-medium">{firmA.name}</Link>
            {" "}offers {formatFirmDisplay(firmA).drawdownFull} with evaluations from {priceA} (50K).{" "}
            <Link to={`/firms/${pair.b}`} className="text-[#1E88E5] hover:underline font-medium">{firmB.name}</Link>
            {" "}offers {formatFirmDisplay(firmB).drawdownFull} with evaluations from {priceB} (50K).
            {" "}Here's how they compare across pricing, drawdown rules, payout speed, and trading restrictions.
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">Can't decide? <Link to="/ask/what-happens-if-you-fail-prop-firm-evaluation" className="text-blue-500 hover:underline">Learn what happens if you fail</Link> — the stakes may be lower than you think.</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mb-6" data-testid="review-links">
            Read full reviews: <Link to={`/reviews/${reviewSlugA}`} className="text-blue-500 hover:underline">{firmA.name} Review</Link>
            {" | "}
            <Link to={`/reviews/${reviewSlugB}`} className="text-blue-500 hover:underline">{firmB.name} Review</Link>
          </p>

          {/* External firm disclaimer */}
          {(isAExternal || isBExternal) && (
            <p className="text-xs text-slate-500 dark:text-slate-500 leading-relaxed mb-8">
              {isAExternal && firmA.disclaimer}{isBExternal && ` ${firmB.disclaimer}`}
              {" "}See all <Link to={isAExternal ? "/topstep-alternatives" : "/apex-alternatives"} className="text-[#1E88E5] hover:underline">alternatives</Link> for a broader comparison.
            </p>
          )}

          {/* Key Differences */}
          <section className="mb-6" data-testid="key-differences">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">Key Differences Between {firmA.name} and {firmB.name}</h2>
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              {firmA.name} uses {formatFirmDisplay(firmA).drawdownFull} while {firmB.name} uses {formatFirmDisplay(firmB).drawdownFull}.
              {" "}On cost, {firmA.name} starts at {priceA} vs {firmB.name} at {priceB} for a 50K evaluation.
              {" "}{firmA.name} offers a {firmA.profit_split || "N/A"} profit split compared to {firmB.name}'s {firmB.profit_split || "N/A"}.
              {" "}For payouts, {firmA.name} pays {firmA.payout_frequency || "varies"} while {firmB.name} pays {firmB.payout_frequency || "varies"}.
            </p>
          </section>

          {/* Quick Verdict */}
          <QuickVerdict firmA={firmA} firmB={firmB} priceA={priceA} priceB={priceB} brandA={brandA} brandB={brandB} />

          {/* Post-Verdict CTAs */}
          <ComparisonCTAs firmA={firmA} firmB={firmB} brandA={brandA} brandB={brandB} pair={pair} testId="comparison-cta-verdict" />

          {/* Quick Comparison Table */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Quick Comparison Table</h2>
            <p className="sm:hidden text-[11px] text-slate-400 dark:text-slate-500 text-right mb-1" data-testid="comparison-scroll-hint">Swipe to compare &rarr;</p>
            <div className="relative">
              <div className="overflow-x-auto -mx-2 px-2">
              <table className="w-full text-sm border-collapse min-w-[520px]" data-testid="comparison-table">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-slate-700">
                    <th className="text-left py-3 px-4 text-xs text-slate-500 uppercase font-semibold w-1/3">Feature</th>
                    <th className="text-left py-3 px-4 text-xs uppercase font-semibold" style={{ color: brandA.brandColor }}>{firmA.name}</th>
                    <th className="text-left py-3 px-4 text-xs uppercase font-semibold" style={{ color: brandB.brandColor }}>{firmB.name}</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Fury Score as first row */}
                  {(() => {
                    const sA = computeFFScore(firmA);
                    const sB = computeFFScore(firmB);
                    const cA = getScoreColor(sA.total);
                    const cB = getScoreColor(sB.total);
                    const ScoreBadge = ({ score, sc, firm, testId }) => (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg border text-sm font-mono font-black cursor-help ${sc.bg} ${sc.text}`} data-testid={testId}>{score.total}<span className="text-[9px] font-medium opacity-60">/10</span></span>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" align="start" className="p-0 bg-slate-900 border-slate-700/60 rounded-xl shadow-xl w-[240px]" data-testid={`fury-tooltip-${testId}`}>
                          <div className="px-3 pt-3 pb-1 border-b border-slate-700/40 flex items-center justify-between">
                            <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">Fury Score</span>
                            <span className={`text-sm font-black ${sc.text}`}>{score.total}<span className="text-[9px] text-slate-500 font-semibold">/10</span></span>
                          </div>
                          <div className="px-3 py-2 space-y-1.5">
                            {COMPONENT_META.filter(({ key }) => score.components[key] !== undefined).map(({ key, label }) => {
                              const val = score.components[key];
                              const reason = getComponentReason(key, val, firm);
                              return (
                                <div key={key}>
                                  <div className="flex items-center justify-between mb-0.5">
                                    <span className="text-[10px] text-slate-400 font-medium">{label}</span>
                                    <span className={`text-[10px] font-mono font-bold ${getScoreColor(val).text}`}>{val}</span>
                                  </div>
                                  <div className="w-full h-1 rounded-full bg-slate-700/60 overflow-hidden">
                                    <div className={`h-full rounded-full ${getBarColor(val)}`} style={{ width: `${val * 10}%` }} />
                                  </div>
                                  <p className="text-[8px] text-slate-500 leading-tight mt-0.5 truncate">{reason}</p>
                                </div>
                              );
                            })}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    );
                    return (
                      <tr className="border-b border-gray-100 dark:border-slate-800 bg-slate-50/80 dark:bg-white/[0.02]">
                        <td className="py-3 px-4 font-semibold text-slate-800 dark:text-slate-200 text-sm">Fury Score</td>
                        <td className="py-3 px-4">
                          <ScoreBadge score={sA} sc={cA} firm={firmA} testId="fury-score-a" />
                        </td>
                        <td className="py-3 px-4">
                          <ScoreBadge score={sB} sc={cB} firm={firmB} testId="fury-score-b" />
                        </td>
                      </tr>
                    );
                  })()}
                  {tableRows.map(([feature, valA, valB], i) => (
                    <tr key={i} className="border-b border-gray-100 dark:border-slate-800">
                      <td className="py-3 px-4 font-medium text-slate-800 dark:text-slate-200">{feature}</td>
                      <td className="py-3 px-4 text-slate-600 dark:text-slate-300">{valA}</td>
                      <td className="py-3 px-4 text-slate-600 dark:text-slate-300">{valB}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              <div className="sm:hidden absolute top-0 right-0 bottom-0 w-8 pointer-events-none bg-gradient-to-l from-white dark:from-[#0f172a] to-transparent" aria-hidden="true" />
            </div>
          </section>

          {/* Post-Table CTAs */}
          <ComparisonCTAs firmA={firmA} firmB={firmB} brandA={brandA} brandB={brandB} pair={pair} testId="comparison-cta-table" />

          {/* Pricing */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">Pricing & Total Cost</h2>
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              {priceA < priceB
                ? `${firmA.name} is the cheaper evaluation, but don't stop at the sticker price.`
                : priceA > priceB
                ? `${firmB.name} is the cheaper evaluation, but don't stop at the sticker price.`
                : `Both firms charge the same for a 50K evaluation, so look beyond the sticker price.`}
              {" "}Activation fees, reset costs, and monthly subscriptions can shift the total. Compare all firms by price on the <Link to="/cheapest-futures-prop-firms" className="text-[#1E88E5] hover:underline">cheapest futures prop firms</Link> page.
            </p>
          </section>

          {/* Drawdown */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">Drawdown Model Comparison</h2>
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              The drawdown model is the single biggest factor in how much room you get during losing streaks. A trailing drawdown tightens as you profit; an EOD or static drawdown resets daily or stays fixed. Understand the difference in the <Link to="/drawdown-types" className="text-[#1E88E5] hover:underline">drawdown types guide</Link> or model your own scenario with the <Link to="/drawdown-calculator" className="text-[#1E88E5] hover:underline">drawdown calculator</Link>.
            </p>
          </section>

          {/* Payout */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">Payout Speed & Profit Split</h2>
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              Payout frequency and profit split directly affect how fast you earn. A firm that pays weekly at 90% lets you compound faster than one paying biweekly at 80%. If payout speed is your priority, see the <Link to="/prop-firms-fast-payouts" className="text-[#1E88E5] hover:underline">fastest payout prop firms</Link> ranking.
            </p>
          </section>

          {/* Rules */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">Trading Rules & Restrictions</h2>
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              News restrictions and consistency rules are where most traders get tripped up. Check the table above for each firm's exact policy — pay special attention to consistency requirements, which can delay your first payout even if you're profitable.
            </p>
          </section>

          {/* Who Should Choose */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">Who Should Choose {firmA.name}?</h2>
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
              Ideal if you're a trader who needs {strengthsA[0] ? strengthsA[0].toLowerCase() : "competitive evaluation pricing"}.{strengthsA[1] ? ` Especially strong for those who want ${strengthsA[1].toLowerCase()}.` : ""}
            </p>

            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">Who Should Choose {firmB.name}?</h2>
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-6">
              Ideal if you're a trader who needs {strengthsB[0] ? strengthsB[0].toLowerCase() : "competitive evaluation pricing"}.{strengthsB[1] ? ` Especially strong for those who want ${strengthsB[1].toLowerCase()}.` : ""}
            </p>
          </section>

          {/* Verdict */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-3">Final Verdict</h2>
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed mb-3">
              {weaknessesA.length > 0
                ? `The catch with ${firmA.name}: ${weaknessesA[0].toLowerCase()}.`
                : `${firmA.name} has no major drawbacks for most traders.`}
              {" "}{weaknessesB.length > 0
                ? `The catch with ${firmB.name}: ${weaknessesB[0].toLowerCase()}.`
                : `${firmB.name} has no major drawbacks for most traders.`}
            </p>
            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
              If neither firm fits perfectly, browse the full <Link to="/best-futures-prop-firms" className="text-[#1E88E5] hover:underline">best futures prop firms</Link> ranking.
              {" "}Or read the full reviews: <Link to={`/reviews/${reviewSlugA}`} className="text-[#1E88E5] hover:underline">{firmA.name}</Link> | <Link to={`/reviews/${reviewSlugB}`} className="text-[#1E88E5] hover:underline">{firmB.name}</Link>.
            </p>
          </section>

          {/* CTAs */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
            {!isAExternal && (
              <div className="border rounded-xl p-4 transition-colors" style={{ borderColor: brandA.brandColor + "40", backgroundColor: brandA.brandColor + "08" }}>
                <Link to={`/reviews/${reviewSlugA}`} className="flex items-center justify-between gap-3 mb-2 hover:opacity-80 transition-opacity">
                  <div>
                    <p className="text-sm font-bold" style={{ color: brandA.brandColor }}>{firmA.name} Review</p>
                    <p className="text-xs text-slate-500 mt-0.5">In-depth verdict, pros & cons</p>
                  </div>
                  <ArrowRight size={16} style={{ color: brandA.brandColor }} className="flex-shrink-0" />
                </Link>
                <Link to={`/firms/${pair.a}`} className="text-xs text-slate-500 dark:text-slate-400 hover:text-blue-500 transition-colors">View pricing & rules →</Link>
              </div>
            )}
            {!isBExternal && (
              <div className="border rounded-xl p-4 transition-colors" style={{ borderColor: brandB.brandColor + "40", backgroundColor: brandB.brandColor + "08" }}>
                <Link to={`/reviews/${reviewSlugB}`} className="flex items-center justify-between gap-3 mb-2 hover:opacity-80 transition-opacity">
                  <div>
                    <p className="text-sm font-bold" style={{ color: brandB.brandColor }}>{firmB.name} Review</p>
                    <p className="text-xs text-slate-500 mt-0.5">In-depth verdict, pros & cons</p>
                  </div>
                  <ArrowRight size={16} style={{ color: brandB.brandColor }} className="flex-shrink-0" />
                </Link>
                <Link to={`/firms/${pair.b}`} className="text-xs text-slate-500 dark:text-slate-400 hover:text-blue-500 transition-colors">View pricing & rules →</Link>
              </div>
            )}
            {isAExternal && (
              <Link to={firmA.slug === "apex" ? "/apex-alternatives" : "/topstep-alternatives"} className="flex items-center justify-between gap-3 bg-slate-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/40 rounded-xl p-4 hover:border-gray-400 transition-colors">
                <div>
                  <p className="text-sm font-bold text-slate-700 dark:text-slate-300">All {firmA.name} Alternatives</p>
                  <p className="text-xs text-slate-500 mt-1">Compare 10+ firms side by side</p>
                </div>
                <ArrowRight size={16} className="text-slate-400 flex-shrink-0" />
              </Link>
            )}
            {isBExternal && (
              <Link to={firmB.slug === "apex" ? "/apex-alternatives" : "/topstep-alternatives"} className="flex items-center justify-between gap-3 bg-slate-50 dark:bg-slate-800/50 border border-gray-200 dark:border-slate-700/40 rounded-xl p-4 hover:border-gray-400 transition-colors">
                <div>
                  <p className="text-sm font-bold text-slate-700 dark:text-slate-300">All {firmB.name} Alternatives</p>
                  <p className="text-xs text-slate-500 mt-1">Compare 10+ firms side by side</p>
                </div>
                <ArrowRight size={16} className="text-slate-400 flex-shrink-0" />
              </Link>
            )}
          </div>

          {/* FAQ */}
          <section className="mb-10">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4">Frequently Asked Questions</h2>
            <div className="space-y-3">
              {faqData.map((f, i) => (
                <details key={i} className="group border border-gray-200 dark:border-white/10 rounded-xl overflow-hidden" data-testid={`faq-item-${i}`}>
                  <summary className="flex items-center justify-between cursor-pointer p-4 sm:p-5 text-sm font-semibold text-slate-900 dark:text-white list-none [&::-webkit-details-marker]:hidden">
                    <h3 className="text-sm font-semibold pr-4">{f.q}</h3>
                    <span className="flex-shrink-0 text-slate-400 transition-transform group-open:rotate-180">&#9662;</span>
                  </summary>
                  <div className="px-4 pb-4 sm:px-5 sm:pb-5">
                    <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{f.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* Useful Guides & Tools */}
          <LinkCardGrid
            testid="comparison-guides"
            title="Useful Guides & Tools"
            subtitle="Understand the rules before you commit to either firm"
            icon={BookOpen}
            iconColor="text-violet-400"
            cols={3}
            cards={[
              { href: "/ask/what-is-trailing-drawdown", category: "ask", title: "What is trailing drawdown?", descriptor: "How the rule that kills most eval accounts actually works.", meta: "4 min read" },
              { href: "/ask/what-is-consistency-rule-prop-trading", category: "ask", title: "Consistency rules explained", descriptor: "Why one big win day can delay your first payout.", meta: "5 min read" },
              { href: "/drawdown-calculator", category: "guide", title: "Drawdown calculator", descriptor: "Model EOD vs trailing scenarios before you buy.", meta: "Tool" },
              { href: "/passing-prop-firm-evaluations", category: "strategy", title: "How to pass evaluations", descriptor: "Discipline framework that actually survives the cut.", meta: "11 min read" },
              { href: "/get-funded-fast", category: "strategy", title: "Get funded in 7–14 days", descriptor: "Which firms let you pass and cash out the fastest.", meta: "7 min read" },
              { href: "/blog/how-to-pass-prop-firm-challenge-fast", category: "strategy", title: "Pass your challenge fast", descriptor: "Compressed-timeline playbook for experienced traders.", meta: "9 min read" },
              { href: "/blog/hidden-rules-that-fail-prop-firm-traders", category: "guide", title: "Hidden rules that fail traders", descriptor: "The fine-print gotchas that aren't on the pricing page.", meta: "8 min read" },
              { href: "/ask/do-prop-firms-send-1099", category: "ask", title: "Do firms send 1099?", descriptor: "US tax paperwork expectations for funded traders.", meta: "3 min read" },
            ]}
          />

          {/* Related Comparisons */}
          <RelatedComparisons relatedA={relatedA} relatedB={relatedB} pair={pair} firmA={firmA} firmB={firmB} />

          <p className="text-[10px] text-slate-400 text-center mt-8 max-w-2xl mx-auto leading-relaxed">
            Data sourced from publicly available firm websites and updated regularly. FuturesFury may earn commissions through affiliate links. Rankings and comparisons remain independent. Always verify rules directly with each firm.
          </p>
        </div>
      </main>
      <Footer />
    </div>
    </TooltipProvider>
  );
}

function ComparisonCTAs({ firmA, firmB, brandA, brandB, pair, testId }) {
  return (
    <div className="grid grid-cols-2 gap-3 mb-10" data-testid={testId}>
      {firmA.affiliate_link ? (
        <div className="flex justify-center">
          <AffiliateCTA href={firmA.affiliate_link} label={`Start ${firmA.name}`} firmSlug={firmA.slug || pair.a} placement="comparison_page" size="md" showSubtext={false} />
        </div>
      ) : (
        <Link to={firmA.isExternal ? `/${pair.a}-alternatives` : `/firms/${pair.a}`} className="flex items-center justify-center gap-2 h-[44px] px-4 rounded-xl text-sm font-semibold border transition-colors hover:opacity-80" style={{ color: brandA.brandColor, borderColor: brandA.brandColor + "40", backgroundColor: brandA.brandColor + "08" }}>
          {firmA.isExternal ? `${firmA.name} Alternatives` : `View ${firmA.name}`} <ArrowRight size={14} />
        </Link>
      )}
      {firmB.affiliate_link ? (
        <div className="flex justify-center">
          <AffiliateCTA href={firmB.affiliate_link} label={`Start ${firmB.name}`} firmSlug={firmB.slug || pair.b} placement="comparison_page" size="md" showSubtext={false} />
        </div>
      ) : (
        <Link to={firmB.isExternal ? `/${pair.b}-alternatives` : `/firms/${pair.b}`} className="flex items-center justify-center gap-2 h-[44px] px-4 rounded-xl text-sm font-semibold border transition-colors hover:opacity-80" style={{ color: brandB.brandColor, borderColor: brandB.brandColor + "40", backgroundColor: brandB.brandColor + "08" }}>
          {firmB.isExternal ? `${firmB.name} Alternatives` : `View ${firmB.name}`} <ArrowRight size={14} />
        </Link>
      )}
    </div>
  );
}

function QuickVerdict({ firmA, firmB, priceA, priceB, brandA, brandB }) {
  const pA = parsePriceUtil(priceA);
  const pB = parsePriceUtil(priceB);
  const payoutRank = { daily: 3, weekly: 2, biweekly: 1 };
  const rankA = payoutRank[firmA.payout_frequency?.split(" ")[0]?.toLowerCase()] || 0;
  const rankB = payoutRank[firmB.payout_frequency?.split(" ")[0]?.toLowerCase()] || 0;
  const flexA = (firmA.news_trading ? 1 : 0) + (firmA.algos_allowed ? 1 : 0);
  const flexB = (firmB.news_trading ? 1 : 0) + (firmB.algos_allowed ? 1 : 0);
  const scoreA = computeFFScore(firmA);
  const scoreB = computeFFScore(firmB);
  const scA = getScoreColor(scoreA.total);
  const scB = getScoreColor(scoreB.total);

  const verdicts = [
    { label: "Best evaluation cost", winner: pA <= pB ? firmA.name : firmB.name, color: pA <= pB ? brandA.brandColor : brandB.brandColor, detail: pA <= pB ? priceA : priceB },
    { label: "Best payout structure", winner: rankA >= rankB ? firmA.name : firmB.name, color: rankA >= rankB ? brandA.brandColor : brandB.brandColor, detail: rankA >= rankB ? firmA.payout_frequency : firmB.payout_frequency },
    { label: "Most flexible rules", winner: flexA >= flexB ? firmA.name : firmB.name, color: flexA >= flexB ? brandA.brandColor : brandB.brandColor, detail: flexA >= flexB ? `${firmA.news_trading ? "News" : "No news"}, ${firmA.algos_allowed ? "algos" : "no algos"}` : `${firmB.news_trading ? "News" : "No news"}, ${firmB.algos_allowed ? "algos" : "no algos"}` },
    { label: "Best profit split", winner: firmA.profit_split > firmB.profit_split ? firmA.name : firmB.name, color: firmA.profit_split > firmB.profit_split ? brandA.brandColor : brandB.brandColor, detail: firmA.profit_split > firmB.profit_split ? firmA.profit_split : firmB.profit_split },
  ];

  return (
    <section className="mb-8 bg-gray-50 dark:bg-slate-800/40 border border-gray-200 dark:border-slate-700/30 rounded-2xl p-5" data-testid="quick-verdict">
      <h2 className="text-base font-bold text-slate-900 dark:text-white mb-4">Quick Verdict</h2>
      {/* Fury Score face-off — prominent rings + breakdowns */}
      <div className="mb-5 py-4 px-4 rounded-xl bg-white dark:bg-slate-900/60 border border-gray-100 dark:border-slate-700/40" data-testid="fury-score-faceoff">
        <div className="flex items-center justify-center gap-6 sm:gap-12 mb-4">
          {[{ firm: firmA, score: scoreA, sc: scA }, { firm: firmB, score: scoreB, sc: scB }].map(({ firm, score, sc }, idx) => (
            <div key={idx} className="flex flex-col items-center gap-1.5">
              <div className="relative w-[56px] h-[56px]">
                <svg className="w-full h-full -rotate-90" viewBox="0 0 56 56">
                  <circle cx="28" cy="28" r="22" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-gray-200 dark:text-slate-700/60" />
                  <circle cx="28" cy="28" r="22" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round"
                    strokeDasharray={2 * Math.PI * 22} strokeDashoffset={2 * Math.PI * 22 * (1 - score.total / 10)}
                    className={`${sc.ring} transition-all duration-700`} />
                </svg>
                <span className={`absolute inset-0 flex items-center justify-center text-base font-black ${sc.text}`}>{score.total}</span>
              </div>
              <span className="text-[10px] font-bold text-slate-600 dark:text-slate-300 text-center leading-tight max-w-[80px] truncate">{firm.name}</span>
              {firm.verified_trader_rating && (
                <span className="text-[9px] text-slate-400 dark:text-slate-500 flex items-center gap-0.5">
                  <Star size={8} className="fill-slate-300 dark:fill-slate-600 text-slate-300 dark:text-slate-600" />
                  {firm.verified_trader_rating}
                </span>
              )}
            </div>
          ))}
        </div>
        <p className="text-[10px] text-slate-400 dark:text-slate-500 text-center uppercase tracking-wider font-semibold mb-3">Fury Score — 5 Dimensions</p>
        {/* Side-by-side breakdown bars */}
        <div className="space-y-2">
          {COMPONENT_META.filter(({ key }) => scoreA.components[key] !== undefined || scoreB.components[key] !== undefined).map(({ key, label }) => {
            const vA = scoreA.components[key] ?? 0;
            const vB = scoreB.components[key] ?? 0;
            return (
              <div key={key} className="flex items-center gap-2" data-testid={`faceoff-dim-${key}`}>
                <span className="text-[10px] text-slate-500 dark:text-slate-400 w-[72px] flex-shrink-0 font-medium text-right">{label}</span>
                <div className="flex-1 flex items-center gap-1.5">
                  <span className={`text-[10px] font-mono font-bold w-[24px] text-right ${getScoreColor(vA).text}`}>{vA || "—"}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-gray-200 dark:bg-slate-700/60 overflow-hidden">
                    <div className={`h-full rounded-full ${getBarColor(vA)} transition-all duration-500`} style={{ width: `${(vA || 0) * 10}%` }} />
                  </div>
                </div>
                <div className="w-px h-3 bg-slate-200 dark:bg-slate-700 flex-shrink-0" />
                <div className="flex-1 flex items-center gap-1.5">
                  <div className="flex-1 h-1.5 rounded-full bg-gray-200 dark:bg-slate-700/60 overflow-hidden">
                    <div className={`h-full rounded-full ${getBarColor(vB)} transition-all duration-500`} style={{ width: `${(vB || 0) * 10}%` }} />
                  </div>
                  <span className={`text-[10px] font-mono font-bold w-[24px] ${getScoreColor(vB).text}`}>{vB || "—"}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {verdicts.map((v, i) => (
          <div key={i} className="flex items-start gap-3 text-sm">
            <Check size={14} className="mt-0.5 flex-shrink-0" style={{ color: v.color }} />
            <div>
              <span className="text-slate-500 dark:text-slate-400">{v.label}: </span>
              <span className="font-semibold" style={{ color: v.color }}>{v.winner}</span>
              {v.detail && <span className="text-slate-400 dark:text-slate-500 text-xs ml-1">({v.detail})</span>}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function RelatedComparisons({ relatedA, relatedB, pair, firmA, firmB }) {
  const allRelated = [...relatedA, ...relatedB]
    .filter((c, i, arr) => arr.findIndex(x => x.slug === c.slug) === i)
    .slice(0, 6);
  if (allRelated.length === 0) return null;

  const cards = allRelated.map((c) => {
    const currentFirmName = c.slug.includes(pair.a) && c.otherSlug !== pair.a ? firmA.name : firmB.name;
    return {
      href: c.url,
      category: "compare",
      title: `${currentFirmName} vs ${c.otherName}`,
      descriptor: "Drawdown, pricing, and payout structure compared side by side.",
      meta: "head-to-head",
    };
  });

  return (
    <LinkCardGrid
      testid="related-comparisons"
      title="Related Comparisons"
      subtitle="Keep comparing — other head-to-heads traders frequently pair with this one"
      icon={GitCompare}
      iconColor="text-blue-400"
      cols={3}
      cards={cards}
    />
  );
}

function ComparisonSkeleton() {
  return (
    <div className="min-h-screen surface-page">
      <Header />
      <main className="pt-28 pb-16 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto animate-pulse" data-testid="comparison-loading">
          <div className="h-10 bg-slate-200 dark:bg-slate-700 rounded-lg w-3/4 mb-4" />
          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full mb-2" />
          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-5/6 mb-8" />
          <div className="h-32 bg-slate-100 dark:bg-slate-800/40 rounded-2xl mb-8" />
          <div className="space-y-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="h-10 bg-slate-100 dark:bg-slate-800/30 rounded" />
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function ComparisonError({ message }) {
  return (
    <div className="min-h-screen surface-page">
      <Header />
      <main className="pt-28 pb-16 px-4 text-center" data-testid="comparison-error">
        <div className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Comparison Unavailable</div>
        <p className="text-slate-500 mb-6">{message || "Unable to load comparison data."}</p>
        <Link to="/best-futures-prop-firms" className="text-[#1E88E5] hover:underline">Browse all firms</Link>
      </main>
      <Footer />
    </div>
  );
}

function NotFoundComparison() {
  useEffect(() => {
    let el = document.querySelector('meta[name="robots"]');
    if (!el) { el = document.createElement("meta"); el.setAttribute("name", "robots"); document.head.appendChild(el); }
    el.setAttribute("content", "noindex, nofollow");
    return () => { if (el) el.setAttribute("content", "index, follow"); };
  }, []);
  return (
    <div className="min-h-screen surface-page">
      <SEO title="Comparison Not Found | FuturesFury" noindex={true} />
      <Header />
      <main className="pt-28 pb-16 px-4 text-center">
        <div className="text-2xl font-bold text-slate-900 dark:text-white mb-4">Comparison Not Found</div>
        <p className="text-slate-500 mb-6">This comparison page doesn't exist.</p>
        <Link to="/best-futures-prop-firms" className="text-[#1E88E5] hover:underline">Browse all firms</Link>
      </main>
      <Footer />
    </div>
  );
}
